// Portfolio content for Jonas Beel — Creative & Communications Strategy Leader.

export const BRAND = {
  name: "Jonas Beel",
  creator: "Jonas Beel",
  role: "Creative · Comms · Web",
  tagline: "Creative & communications strategy",
  based: "Hudsonville, MI",
  phone: "616-889-6680",
  linkedin: "https://www.linkedin.com/in/jonas-beel/",
  resume: "/jonas-beel-resume.pdf",
};

export const NAV_LINKS = [
  { label: "Work", href: "#work" },
  { label: "Services", href: "#services" },
  { label: "Experience", href: "#experience" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

// Scroll-synced hero states.
export const HERO_STATES = [
  {
    kicker: "Jonas Beel · Creative & Communications",
    line1: "Digital and visual work",
    accent: "that connects.",
    sub: "Web design, UX/UI, branding, front-end and marketing — brought together with strategy so the message actually lands.",
  },
  {
    kicker: "Design · Web · Marketing · Comms",
    line1: "Clarity, creativity,",
    accent: "and strategy.",
    sub: "Fifteen-plus years turning ideas into websites, campaigns, brands and content systems that help organizations communicate clearly.",
  },
];

export const SERVICES = [
  {
    no: "01",
    title: "Web Design & Front-End",
    body: "Clean, responsive websites and landing pages that pair strong visuals with clear structure and practical front-end execution.",
    tags: ["Responsive", "HTML/CSS", "WordPress"],
    detail:
      "I design and build sites the whole way through — layout, visual language and the responsive HTML/CSS that makes it real. Landing pages and lead-capture pages are built with usability, SEO and conversion in mind, so audiences find what they need and know exactly what to do next.",
  },
  {
    no: "02",
    title: "UX/UI & Digital Experience",
    body: "User-friendly interfaces, dashboards, workflows and web experiences designed around clarity and real usability.",
    tags: ["UX/UI", "Figma", "Product"],
    detail:
      "For learning platforms, member areas and back-end applications I map the flows first, then design interfaces that are easy to navigate and genuinely usable. The goal is always the same: reduce friction, improve accessibility and make the experience feel considered on every screen.",
  },
  {
    no: "03",
    title: "Brand, Print & Visual Design",
    body: "Brand systems, print materials, event graphics, social media, presentations and campaign creative — all visually consistent.",
    tags: ["Identity", "Print", "Guidelines"],
    detail:
      "I build brand systems and the guidelines that keep them intact — typography, color, layout and voice — then produce everything from logos and event collateral to social graphics and keynote decks. It gives teams one clear framework and keeps every touchpoint on-brand.",
  },
  {
    no: "04",
    title: "Digital Marketing & Conversion",
    body: "Lead-capture pages, campaign assets, ad creative and conversion-focused design that support real marketing goals.",
    tags: ["Meta", "Landing pages", "Ads"],
    detail:
      "I plan and produce Meta, Instagram and email campaigns end to end — the graphics, the video assets and the messaging that hold them together — with a focus on capturing leads and converting attention into action.",
  },
  {
    no: "05",
    title: "Email Marketing & Automation",
    body: "Campaigns, newsletters and automated customer journeys built in AWeber, Keap, ActiveCampaign and Zapier.",
    tags: ["AWeber", "Keap", "Zapier"],
    detail:
      "I connect landing pages, forms, email platforms and Zapier workflows into automations that handle follow-up and nurturing on their own — plus the newsletter and campaign design that keeps ongoing communication looking sharp.",
  },
  {
    no: "06",
    title: "Communications & Content",
    body: "Visual storytelling, campaign messaging, presentation design and cross-channel creative that helps organizations communicate clearly.",
    tags: ["Messaging", "Decks", "Video"],
    detail:
      "I help organizations say things clearly — through written content, presentation design, curriculum and video, and creative that carries across web, email, social and live events. It's communication strategy translated into things people actually see and use.",
  },
];

export const PROJECTS = [
  {
    title: "LaunchYou",
    category: "Brand & Product",
    year: "2026",
    summary:
      "Ongoing lead designer for a fast-moving education and coaching brand — brand guidelines, UI/UX for web and back-end apps, responsive HTML/CSS sales pages, plus presentations, emails and curriculum video. My role: design lead & brand specialist keeping every channel visually consistent.",
    image: "/projects/launchyou-1.webp",
    gallery: [
      "/projects/launchyou-1.webp",
      "/projects/launchyou-7.webp",
      "/projects/launchyou-3.webp",
      "/projects/launchyou-6.webp",
      "/projects/launchyou-4.webp",
      "/projects/launchyou-2.webp",
      "/projects/launchyou-5.webp",
    ],
  },
  {
    title: "ADHD Online",
    category: "UX/UI · Product",
    year: "2025",
    summary:
      "A telehealth experience for ADHD diagnosis and treatment — from the marketing site and assessment flow to the patient portal. My role: UX/UI and interface design, mapping and prototyping the journey end to end for clarity and accessibility.",
    image: "/projects/adhd-5.webp",
    gallery: [
      "/projects/adhd-5.webp",
      "/projects/adhd-6.webp",
      "/projects/adhd-3.webp",
      "/projects/adhd-4.webp",
      "/projects/adhd-2.webp",
    ],
  },
  {
    title: "Key Green Solutions",
    category: "Web & Interface",
    year: "2025",
    summary:
      "A sustainability reporting platform for healthcare — marketing site, mobile experience and a reporting dashboard that turns vendor data into clear insight. My role: web and interface design focused on making dense data easy to read and act on.",
    image: "/projects/keygreen-2.webp",
    gallery: [
      "/projects/keygreen-2.webp",
      "/projects/keygreen-3.webp",
      "/projects/keygreen-1.webp",
    ],
  },
  {
    title: "Etsy Product Images",
    category: "Visual & E-commerce",
    year: "2025",
    summary:
      "A scroll-stopping product image and label system for a soy-candle brand — clean studio renders and seasonal scenes designed to convert on a crowded Etsy search page. My role: visual design and product presentation built for e-commerce.",
    image: "/projects/etsy-2.webp",
    gallery: [
      "/projects/etsy-2.webp",
      "/projects/etsy-1.webp",
      "/projects/etsy-3.webp",
      "/projects/etsy-4.webp",
      "/projects/etsy-5.webp",
    ],
  },
  {
    title: "AI Avatar",
    category: "AI · Content · Growth",
    year: "2025",
    summary:
      "An AI-generated avatar and short-form content engine — consistent, on-brand personas published to Instagram and TikTok to drive views, comments and follows. My role: concept, avatar and content system design, and the publishing workflow behind it.",
    image: "/projects/aiavatar-1.webp",
    gallery: [
      "/projects/aiavatar-1.webp",
      "/projects/aiavatar-2.webp",
      "/projects/aiavatar-3.webp",
      "/projects/aiavatar-4.webp",
    ],
  },
  {
    title: "Self-Publishing",
    category: "Author · Brand · Growth",
    year: "2024",
    summary:
      "Two self-published parenting titles, taken end to end solo — written, cover and interior designed, and marketed myself. A Meta ad funnel built around the series captured 2,000+ qualified reader leads. My role: author, designer and growth marketer.",
    image: "/projects/publishing-1.webp",
    gallery: [
      "/projects/publishing-1.webp",
      "/projects/publishing-2.webp",
      "/projects/book-1.webp",
      "/projects/book-2.webp",
      "/projects/book-3.webp",
      "/projects/book-5.webp",
      "/projects/book-4.webp",
    ],
  },
  {
    title: "SleepWave",
    category: "UX/UI · Mobile",
    year: "2026",
    summary:
      "A sleep-tracking mobile app centered on turning nightly data into clear, calming insight — sleep stages, trends and gentle guidance visualized so users actually understand their rest. My role: UX/UI and interface design for the full mobile experience and its data visualization.",
    image: "/projects/sleepwave-1.webp",
    gallery: [
      "/projects/sleepwave-1.webp",
      "/projects/sleepwave-2.webp",
      "/projects/sleepwave-3.webp",
      "/projects/sleepwave-4.webp",
    ],
  },
];

export const EXPERIENCE = [
  {
    company: "Code Green Creative, LLC",
    role: "Founder / Lead Designer",
    period: "2007 — Present",
    body: "Founded and lead a creative web and design studio, running projects from discovery to launch. I partner directly with clients to clarify goals, brand and messaging, then design and build custom websites, landing pages and campaigns with usability, SEO and conversion in mind — and stay on as their creative advisor.",
    tags: ["Web", "Brand", "Meta & email campaigns"],
  },
  {
    company: "Launch You",
    role: "Contracted Lead Designer & Brand Specialist",
    period: "2021 — Present",
    body: "Ongoing design partner to an education and coaching brand. I built the brand guidelines, design UI/UX for websites and back-end apps, convert concepts into responsive HTML/CSS sales and lead-capture pages, and produce presentations, emails, study guides and curriculum video that keep messaging consistent.",
    tags: ["UI/UX", "HTML/CSS", "Video"],
  },
  {
    company: "Encoursa",
    role: "Co-Founder / Lead Design & Brand Development",
    period: "2014 — 2022",
    body: "Co-founded a continuing-education platform for accounting and tax professionals and led its brand and design. I created the identity, logos, newsletters and ad assets, and led UX/UI for the learning platform — layouts and flows that improved navigation, accessibility and learner engagement.",
    tags: ["Brand", "LMS UX/UI", "Marketing"],
  },
  {
    company: "Digital Experts Academy",
    role: "Contracted Web & Print Designer & Brand Developer",
    period: "2009 — 2021",
    body: "Supported a large member-based education organization across web, print, event and brand design. I built and optimized lead-capture pages, connected forms, Zapier workflows and email platforms (AWeber, Keap) into automated follow-up, and designed keynotes, PDFs, logos and event collateral to a consistent standard.",
    tags: ["Landing pages", "Automation", "Events"],
  },
];

export const SKILLS = [
  {
    group: "Design & Creative",
    items: ["Adobe Creative Suite", "Photoshop · Illustrator · InDesign", "Figma", "Canva", "Brand development", "Print & social design", "Presentation design", "Video editing"],
  },
  {
    group: "Web & UX/UI",
    items: ["Responsive web design", "UX/UI design", "Front-end HTML / CSS", "WordPress & CMS", "Landing pages", "Digital product design"],
  },
  {
    group: "Marketing & Communications",
    items: ["Digital marketing", "Conversion optimization", "Email & newsletters", "Content strategy", "Campaign creative", "Stakeholder communication"],
  },
  {
    group: "Automation & Platforms",
    items: ["AWeber", "Keap", "ActiveCampaign", "Zapier", "Workflow automation", "AI tools"],
  },
];

// Practical outcomes — "How I help / Where I bring value".
export const PHILOSOPHY =
  "Good creative work should do more than look polished — it should make communication clearer, guide people toward action, and support the goals of the organization behind it.";

export const HOW_I_HELP = [
  "Clarify brand, message and visual identity",
  "Improve websites and digital experiences",
  "Design user-friendly interfaces and workflows",
  "Create polished marketing and campaign assets",
  "Build email and automation workflows",
  "Turn ideas into usable creative deliverables",
];

export const STATS = [
  { value: 17, suffix: "+", label: "Years in creative & comms" },
  { value: 16, suffix: "+", label: "Brands / clients built" },
  { value: 6, suffix: "", label: "Core disciplines" },
  { value: 100, suffix: "%", label: "Remote-ready" },
];

export const ABOUT = {
  portrait: "https://customer-assets.emergentagent.com/job_web-gateway-launch/artifacts/cwbc6jb9_me.jpg",
  name: "Jonas Beel",
  role: "Creative & Communications Strategy Leader",
  body: "I bring design, development, marketing and communication to help organizations create clearer digital experiences and stronger visual communication. Seventeen-plus years — as a studio founder, co-founder and long-term design partner — I've built brands, websites, campaigns and content systems across web, email, social, print and live events. I'm comfortable moving from strategy to a finished, responsive build, and just as comfortable in the room with stakeholders translating business goals into work that connects.",
  facts: [
    { k: "Based", v: "Hudsonville, MI" },
    { k: "Experience", v: "17+ years" },
    { k: "Education", v: "BBA Marketing, GVSU" },
    { k: "Focus", v: "Web · Brand · Comms" },
  ],
};

export const TICKER = [
  "Web design",
  "UX/UI",
  "Front-end",
  "Brand systems",
  "Email & automation",
  "Digital marketing",
  "Print & events",
  "Presentation design",
  "Video & content",
];
